import { t } from "@travily/i18n";
import { Frame } from "@ui/components/Frame";
import { Heading } from "@ui/components/Heading";
import { Icon } from "@ui/components/Icon";
import { SoftButton } from "@ui/components/SoftButton";
import { TopBarBorder } from "@ui/components/TopBar/TopBarBorder";
import { TopBarMobile } from "@ui/components/TopBar/TopBarMobile";
import { tv } from "@ui/lib/tv";
import { Link } from "@ui/primitives/Link";
import type { ReactNode } from "react";
import { type EditorNavSection, EditorSectionNav } from "@/components/EditorSectionNav/EditorSectionNav";
import { PageFrame } from "@/layouts/page-frame";

const editFrameStyles = tv({
  slots: {
    sidebarContent: "flex flex-col gap-6 p-5 md:p-8",
    sidebarHeader: "hidden items-center gap-3 lg:flex",
    contentPadding: "flex flex-1 flex-col p-5 md:p-8",
    contentWrapper: "mx-auto flex w-full max-w-2xl flex-1 flex-col",
    actionsBar: "sticky bottom-0 border-neutral-100 border-t bg-white p-4 lg:p-5",
  },
});

export type EditSection = EditorNavSection;

export interface EditFrameProps {
  /** Bottom action bar — typically a `ButtonDock` with a Save button. */
  actions?: ReactNode;
  /** Currently-active section key. When unset, the page is the section index. */
  activeSection?: string;
  /** Where the back arrow goes from the sidebar header on the index view. */
  backHref: string;
  children?: ReactNode;
  /** href of the parent index page; back-href on a section detail page. */
  href: string;
  /** How the section nav renders — `card` previews each section, `row` is the
   *  compact icon+label list. */
  navLayout?: "card" | "row";
  sections: EditSection[];
  /** Page heading shown in the sidebar header and as the mobile top bar title on the index. */
  title: string;
}

/**
 * Section-based editor frame for products and services. Wraps `PageFrame` so editors
 * inherit the standard top bar (creator-mode chrome, account switcher, notifications)
 * and footer alerts. Section navigation is a left pane on desktop; on mobile each
 * section page navigates with its own back arrow.
 *
 * Lives in `app/frontend/layouts/` because it composes the app-only `PageFrame`. Both
 * Product and Service editors inherit `Listing` on the backend and share this chrome.
 */
export function EditFrame({
  actions,
  activeSection,
  backHref,
  children,
  href,
  navLayout = "card",
  sections,
  title,
}: EditFrameProps) {
  const styles = editFrameStyles();
  const activeSectionConfig = activeSection ? sections.find((s) => s.key === activeSection) : undefined;

  const sidebar = (
    <div className={styles.sidebarContent()}>
      <div className={styles.sidebarHeader()}>
        <Link href={backHref}>
          <SoftButton aria-label={t("components.frames.editor_layout.back")}>
            <Icon name="arrowLeft" size="16" />
          </SoftButton>
        </Link>
        <Heading.Root color="primary" size="lg">
          <Heading.Title as="h2">{title}</Heading.Title>
        </Heading.Root>
      </div>
      <EditorSectionNav ariaLabel={title} currentKey={activeSection} layout={navLayout} sections={sections} />
    </div>
  );

  const mobileTopBar = activeSection ? (
    <TopBarMobile backHref={href} bordered title={activeSectionConfig?.label} />
  ) : (
    <TopBarMobile backHref={backHref} bordered title={title} />
  );

  return (
    <PageFrame
      contentMaxWidth="full"
      contentPadded={false}
      footerFullBleed
      hideBottomBar
      hideFooter
      hideTopBarNav
      mode="creator"
      mobileTopBar={mobileTopBar}
      topBarBottomContent={<TopBarBorder />}
    >
      {activeSection && (
        <Frame.LeftPane collapse="drill" maxWidth="medium">
          {sidebar}
        </Frame.LeftPane>
      )}

      {activeSection ? (
        <div className="flex flex-1 flex-col">
          <div className={styles.contentPadding()}>
            <div className={styles.contentWrapper()}>{children}</div>
          </div>
          {actions ? <div className={styles.actionsBar()}>{actions}</div> : null}
        </div>
      ) : (
        <div className="flex flex-1 flex-col md:flex-row">
          <div className="md:w-sm md:flex-shrink-0 md:border-neutral-100 md:border-r">{sidebar}</div>
        </div>
      )}
    </PageFrame>
  );
}
